import { Component, Input, SimpleChanges } from '@angular/core';
import { TreeNode } from 'primeng/api';

import { CourseLessonEntity } from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { LessonSelectionTreeControlComponent } from '../lesson-selection-tree-control/lesson-selection-tree-control.component';
import {
  AssignmentResetImpact,
  AssignmentResetLessonImpact,
  AssignmentResetStudentImpact
} from '../../../schools/models/assignment-reset-impact.model';

interface LessonSelectionListLessonRow {
  readonly id: number;
  readonly label: string;
  readonly selected: boolean;
  readonly durationLabel: string;
  readonly icon: string;
  readonly resetWarning: boolean;
  readonly loading: boolean;
  readonly resetCount: number;
  readonly resetStudentNames: string;
  readonly completedStudentNames: string;
  readonly completedStudentCount: number;
  readonly showProgressCount: boolean;
  readonly showResetCount: boolean;
}

interface LessonSelectionListModuleRow {
  readonly id: number;
  readonly label: string;
  readonly selectedCount: number;
  readonly totalCount: number;
  readonly checked: boolean;
  readonly partial: boolean;
  readonly impactLabel: string;
  readonly resetWarning: boolean;
  readonly resetStudentNames: string;
  readonly completedStudentCount: number;
  readonly completedStudentNames: string;
  readonly showProgressCount: boolean;
  expanded: boolean;
  readonly lessons: LessonSelectionListLessonRow[];
}

@Component({
  selector: 'app-lesson-selection-list-control',
  templateUrl: './lesson-selection-list-control.component.html',
  styleUrls: ['./lesson-selection-list-control.component.scss']
})
export class LessonSelectionListControlComponent extends LessonSelectionTreeControlComponent {
  @Input() resetImpact: AssignmentResetImpact;

  private readonly defaultExpandedModuleCount = 1;

  moduleRows: LessonSelectionListModuleRow[] = [];
  selectedLessonCount = 0;

  constructor(courseRepositoryService: CourseRepositoryService) {
    super(courseRepositoryService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.resetImpact) {
      this.buildModuleRows();
    }
  }

  updateSelectedNodes(expandNodes: boolean = false) {
    super.updateSelectedNodes(expandNodes);
    this.buildModuleRows();
  }

  buildTree() {
    super.buildTree();
    this.expandFirstIncompleteModules();
    this.buildModuleRows();
  }

  toggleModuleExpanded(moduleRow: LessonSelectionListModuleRow) {
    moduleRow.expanded = !moduleRow.expanded;

    const moduleNode = this.treeData.find(node => node.data === moduleRow.id);
    if (moduleNode) {
      moduleNode.expanded = moduleRow.expanded;
    }
  }

  toggleModuleSelection(moduleRow: LessonSelectionListModuleRow) {
    const selectedLessonIdSet = new Set<number>(this.selectedLessonIds || []);

    for (const lesson of moduleRow.lessons) {
      if (moduleRow.checked) {
        selectedLessonIdSet.delete(lesson.id);
      } else {
        selectedLessonIdSet.add(lesson.id);
      }
    }

    this.emitSelection(Array.from(selectedLessonIdSet));
  }

  toggleLessonSelection(moduleRow: LessonSelectionListModuleRow, lessonRow: LessonSelectionListLessonRow) {
    const selectedLessonIdSet = new Set<number>(this.selectedLessonIds || []);

    if (lessonRow.selected) {
      selectedLessonIdSet.delete(lessonRow.id);
    } else {
      selectedLessonIdSet.add(lessonRow.id);
    }

    const moduleNode = this.treeData.find(node => node.data === moduleRow.id);
    if (moduleNode) {
      moduleNode.expanded = moduleRow.expanded;
    }

    this.emitSelection(Array.from(selectedLessonIdSet));
  }

  trackModule(index: number, moduleRow: LessonSelectionListModuleRow): number {
    return moduleRow.id;
  }

  trackLesson(index: number, lessonRow: LessonSelectionListLessonRow): number {
    return lessonRow.id;
  }

  private emitSelection(lessonIds: number[]) {
    this.selectedLessonIds = lessonIds;
    this.updateSelectedNodes();
    this.selectedLessonIdsChange.emit(this.selectedLessonIds);
  }

  private buildModuleRows() {
    const selectedLessonIdSet = new Set<number>(this.selectedLessonIds || []);

    this.moduleRows = (this.treeData || []).map(moduleNode => {
      const lessons = (moduleNode.children || []).map(lessonNode => this.buildLessonRow(lessonNode, selectedLessonIdSet));
      const selectedCount = lessons.filter(lesson => lesson.selected).length;
      const totalCount = lessons.length;
      const moduleImpact = (this.resetImpact?.Modules || []).find(impact => impact.CourseModuleId === moduleNode.data);
      const completedStudents = this.getCompletedModuleStudents(moduleNode.data, lessons.map(lesson => lesson.id));

      return {
        id: moduleNode.data,
        label: moduleNode.label,
        selectedCount,
        totalCount,
        checked: totalCount > 0 && selectedCount === totalCount,
        partial: selectedCount > 0 && selectedCount < totalCount,
        impactLabel: this.resetImpact?.WillResetProgress ? this.formatModuleImpact(moduleImpact) : '',
        resetWarning: !!this.resetImpact?.WillResetProgress && (moduleImpact?.StudentLessonCount || 0) > 0,
        resetStudentNames: this.formatModuleStudentNames(moduleNode.data),
        completedStudentCount: completedStudents.length,
        completedStudentNames: completedStudents.length ? `Completed: ${completedStudents.map(student => student.DisplayName).join(', ')}` : '',
        showProgressCount: this.showStudentCounts() && completedStudents.length > 0,
        expanded: moduleNode.expanded,
        lessons
      };
    });

    this.selectedLessonCount = this.moduleRows.reduce((sum, moduleRow) => sum + moduleRow.selectedCount, 0);
  }

  private expandFirstIncompleteModules() {
    let expandedIncompleteModuleCount = 0;

    for (const moduleNode of this.treeData || []) {
      const moduleProgress = this.studentCourse?.CourseModuleProgress?.find(mp => mp.CourseModuleId === moduleNode.data);
      const isIncomplete = !this.studentCourse || !moduleProgress || (moduleProgress.PercentComplete || 0) < 100;

      moduleNode.expanded = isIncomplete && expandedIncompleteModuleCount < this.defaultExpandedModuleCount;

      if (isIncomplete) {
        expandedIncompleteModuleCount++;
      }
    }
  }

  private buildLessonRow(lessonNode: TreeNode, selectedLessonIdSet: Set<number>): LessonSelectionListLessonRow {
    const lesson = this.findLesson(lessonNode.data);
    const impact = (this.resetImpact?.Lessons || []).find(item => item.CourseLessonId === lessonNode.data);
    const selected = selectedLessonIdSet.has(lessonNode.data);

    return {
      id: lessonNode.data,
      label: lessonNode.label,
      selected,
      durationLabel: this.formatLessonDuration(lesson),
      icon: (impact?.CompletedStudentCount || 0) > 0 ? 'fa fa-check-circle dw-green' : lessonNode.icon,
      resetWarning: selected && !!this.resetImpact?.WillResetProgress && (impact?.StudentCount || 0) > 0,
      loading: selected && (this.resetImpact?.LoadingCourseLessonIds || []).indexOf(lessonNode.data) >= 0,
      resetCount: impact?.StudentCount || 0,
      resetStudentNames: this.formatResetStudentNames(impact),
      completedStudentNames: this.formatCompletedStudentNames(impact),
      completedStudentCount: impact?.CompletedStudentCount || 0,
      showProgressCount: this.showStudentCounts() && (impact?.CompletedStudentCount || 0) > 0,
      showResetCount: selected
        && this.showStudentCounts()
        && (impact?.StudentCount || 0) > 0
    };
  }

  private getCompletedModuleStudents(courseModuleId: number, lessonIds: number[]): AssignmentResetStudentImpact[] {
    const lessonImpacts = (this.resetImpact?.Lessons || [])
      .filter(lesson => lesson.CourseModuleId === courseModuleId && lessonIds.indexOf(lesson.CourseLessonId) >= 0);

    if (!lessonIds.length || lessonImpacts.length !== lessonIds.length) {
      return [];
    }

    const completedByStudentId = new Map<number, AssignmentResetStudentImpact>();
    (lessonImpacts[0].Students || [])
      .filter(student => student.Completed)
      .forEach(student => completedByStudentId.set(student.StudentId, student));

    lessonImpacts.slice(1).forEach(lesson => {
      const completedStudentIds = new Set((lesson.Students || [])
        .filter(student => student.Completed)
        .map(student => student.StudentId));
      Array.from(completedByStudentId.keys())
        .filter(studentId => !completedStudentIds.has(studentId))
        .forEach(studentId => completedByStudentId.delete(studentId));
    });

    return Array.from(completedByStudentId.values());
  }

  private showStudentCounts(): boolean {
    return (this.resetImpact?.EvaluatedStudentCount || 0) > 1;
  }

  private formatCompletedStudentNames(impact: AssignmentResetLessonImpact): string {
    const studentNames = Array.from(new Set((impact?.Students || [])
      .filter(student => student.Completed)
      .map(student => student.DisplayName)));

    return studentNames.length ? `Completed: ${studentNames.join(', ')}` : '';
  }

  private formatResetStudentNames(impact: AssignmentResetLessonImpact): string {
    const studentNames = Array.from(new Set((impact?.Students || []).map(student => student.DisplayName)));

    return studentNames.length ? `Reset lessons for ${studentNames.join(', ')}` : '';
  }

  private formatModuleImpact(moduleImpact: { StudentCount: number; LessonCount: number; StudentLessonCount: number }): string {
    if (!moduleImpact?.StudentLessonCount) {
      return '';
    }

    const lessonText = moduleImpact.LessonCount === 1 ? '1 lesson' : `${moduleImpact.LessonCount} lessons`;
    const studentText = moduleImpact.StudentCount === 1 ? '1 student' : `${moduleImpact.StudentCount} students`;
    return `Reset ${lessonText} / ${studentText}`;
  }

  private formatModuleStudentNames(courseModuleId: number): string {
    const studentNames = new Set<string>();
    (this.resetImpact?.Lessons || [])
      .filter(lesson => lesson.Selected && lesson.CourseModuleId === courseModuleId)
      .forEach(lesson => (lesson.Students || []).forEach(student => studentNames.add(student.DisplayName)));
    return Array.from(studentNames).join(', ');
  }

  private findLesson(courseLessonId: number): CourseLessonEntity {
    for (const courseModule of this.course?.CourseModule || []) {
      const lesson = courseModule.CourseLesson?.find(l => l.CourseLessonId === courseLessonId);
      if (lesson) {
        return lesson;
      }
    }

    return null;
  }

  private formatLessonDuration(lesson: CourseLessonEntity): string {
    const durationSeconds = (lesson as any)?.Video?.VideoDurationSeconds
      || ((lesson as any)?.Quiz?.TimeLimitMinutes ? (lesson as any).Quiz.TimeLimitMinutes * 60 : 0);

    if (!durationSeconds) {
      return '';
    }

    const minutes = Math.max(1, Math.round(durationSeconds / 60));

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
    }

    return `${minutes} min`;
  }
}
